<template>
  <!--
    Ledger help guide — slide-in modal shown from the ledger page for
    branch (subadmin) and client roles. Content is role-aware: the
    direction rules for pay-in / pay-out / commission flip depending on
    whose perspective you're reading (client sees "money in = credit";
    branch sees "money in = debit" because they're holding it for the
    vendor's client). Vendor roles never see this modal — they have the
    full ledger index instead.

    Teleported to body so it stacks cleanly above the ledger page's own
    z-index layers (balance card, toast, etc.).
  -->
  <teleport to="body">
    <transition name="help-fade">
      <div v-if="show" class="help-backdrop" @click.self="$emit('close')">
        <div class="help-panel" role="dialog" aria-modal="true" aria-labelledby="help-title">
          <header class="help-head">
            <div>
              <div class="help-eyebrow">Help &amp; Guide</div>
              <h2 id="help-title" class="help-title">Understanding your ledger</h2>
              <p class="help-sub">
                {{ roleLabel }} view — how your balances are built and when they update.
              </p>
            </div>
            <button type="button" class="help-close" @click="$emit('close')" aria-label="Close">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
                <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" />
              </svg>
            </button>
          </header>

          <nav class="help-toc" aria-label="Sections">
            <a href="#sec-overview" class="toc-link">Overview</a>
            <a href="#sec-balances" class="toc-link">Balances</a>
            <a href="#sec-directions" class="toc-link">Direction rules</a>
            <a href="#sec-categories" class="toc-link">Categories</a>
            <a href="#sec-schedule" class="toc-link">When it updates</a>
            <a href="#sec-faq" class="toc-link">FAQ</a>
          </nav>

          <div class="help-body">
            <!-- =============================================================
                 Overview
            ============================================================== -->
            <section id="sec-overview" class="help-section">
              <h3>Overview</h3>
              <p>
                This ledger records every rupee that has moved between you and your
                account manager — settled figures on the left, activity still awaiting
                settlement on the right.
              </p>
            </section>

            <!-- =============================================================
                 Balances
            ============================================================== -->
            <section id="sec-balances" class="help-section">
              <h3>Reading your balances</h3>
              <div class="hint-grid">
                <div class="hint-card">
                  <div class="hint-label">Settled balance</div>
                  <p>
                    Everything the system has automatically settled at 12:00 AM (midnight)
                    IST, plus any manual entries your account manager has posted. This
                    figure won't change until the next automatic settlement or a manual
                    adjustment.
                  </p>
                </div>
                <div class="hint-card">
                  <div class="hint-label">Pending balance</div>
                  <p>
                    Your approved {{ orderNoun }} orders since the last settlement — netted
                    into a single number using your own direction rules (see below).
                    This is what your live balance will move by after tonight's automatic
                    settlement at 12:00 AM IST.
                  </p>
                </div>
                <div class="hint-card">
                  <div class="hint-label">Live balance</div>
                  <p>
                    <b>Settled + Pending.</b> Your best up-to-the-minute estimate of what
                    your account manager holds on your behalf {{ isClient ? '' : '(or owes you back)' }}.
                  </p>
                </div>
              </div>
            </section>

            <!-- =============================================================
                 Direction rules — role-specific
            ============================================================== -->
            <section id="sec-directions" class="help-section">
              <h3>Direction rules</h3>
              <p>
                Every ledger entry is either a <b>credit</b> (adds to your balance) or a
                <b>debit</b> (subtracts). The direction depends on the event and who's
                looking at it — this table shows how each event lands on
                <em>{{ roleLabel.toLowerCase() }}</em> books:
              </p>
              <table class="dir-table">
                <thead>
                  <tr>
                    <th>Event</th>
                    <th>Direction</th>
                    <th>Why</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in directionRows" :key="row.event">
                    <td class="dir-event">{{ row.event }}</td>
                    <td>
                      <span class="dir-pill" :class="'dir-' + row.direction">
                        {{ row.direction }}
                      </span>
                    </td>
                    <td class="dir-why">{{ row.why }}</td>
                  </tr>
                </tbody>
              </table>
              <div class="callout callout-mute">
                <b>Amounts are always stored positive.</b> The <em>direction</em> column
                on each entry tells you which way to read it. Don't multiply by a sign
                yourself — the running balance already accounts for it.
              </div>
            </section>

            <!-- =============================================================
                 Categories
            ============================================================== -->
            <section id="sec-categories" class="help-section">
              <h3>Entry categories</h3>
              <ul class="cat-list">
                <li>
                  <b>Opening Balance</b> — the "as-of" figure your account manager posted
                  once at onboarding. Anchors every calculation that follows.
                </li>
                <li>
                  <b>Settlement</b> — one row per day, posted automatically at 12:00 AM
                  IST, capturing that day's net {{ orderNoun }} activity and commission.
                </li>
                <li>
                  <b>Reversal</b> — automatic offset when an order that had already
                  settled is later marked failed or rejected. Points back at the original
                  settlement row so the trail stays clear.
                </li>
                <li>
                  <b>Bonus / Penalty / Correction</b> — manual entries your account
                  manager can post at any time. Always visible with their name in the
                  <em>by</em> column.
                </li>
                <li>
                  <b>Deposit / Withdrawal</b> — reflect cash you've put in or taken out
                  outside the order flow (bank transfer top-ups, physical withdrawal, etc.).
                </li>
              </ul>
            </section>

            <!-- =============================================================
                 When it updates
            ============================================================== -->
            <section id="sec-schedule" class="help-section">
              <h3>When it updates</h3>
              <ul class="tight-list">
                <li>
                  <b>Automatic settlement — every night at 12:00 AM IST.</b>
                  The system picks up every approved {{ orderNoun }} order from the
                  previous day and posts a single settlement row for you.
                </li>
                <li>
                  <b>Automatic reversal — same 12:00 AM run.</b> If an order that had
                  settled the day before is now marked failed or rejected, an offset
                  row is posted alongside it, referencing the original.
                </li>
                <li>
                  <b>Manual entries.</b> Land instantly the moment your account manager
                  submits them — no wait for the nightly run.
                </li>
                <li>
                  <b>Pending balance.</b> Re-calculated live every time you open this
                  page — always up to the minute, never cached.
                </li>
              </ul>
            </section>

            <!-- =============================================================
                 FAQ
            ============================================================== -->
            <section id="sec-faq" class="help-section">
              <h3>FAQ</h3>
              <details class="faq">
                <summary>My live balance changed but I don't see a new entry yet.</summary>
                <p>
                  That's expected — your <em>pending</em> figure recalculates every time
                  you open this page, but the entry itself only appears after tonight's
                  automatic settlement at 12:00 AM IST. Refresh in the morning and you'll
                  see the new settled row.
                </p>
              </details>
              <details class="faq">
                <summary>Why doesn't my pending figure match my order dashboard?</summary>
                <p>
                  Pending only counts orders in the "approved" state within the
                  post-opening-balance window, and it nets your commission.
                  The order dashboard is a raw count — it doesn't subtract commission
                  or apply direction rules.
                </p>
              </details>
              <details class="faq">
                <summary>Can I post my own manual entry?</summary>
                <p>
                  No — only your account manager can post manual entries. If a figure
                  looks wrong, reach out to them and they'll post a Correction row
                  (which will show up here immediately, tagged with their name).
                </p>
              </details>
              <details class="faq">
                <summary>What happens on the day my opening balance was posted?</summary>
                <p>
                  Orders approved from that day, starting at 12:00 AM IST, are counted
                  in your ledger from then on. Anything before is already baked into
                  the opening balance itself, so it's never counted twice.
                </p>
              </details>
              <details class="faq">
                <summary>Does the ledger ever change past entries?</summary>
                <p>
                  No — every entry is immutable. Reversals are posted as new rows that
                  point at the original; corrections come in as their own rows too.
                  This gives you a full audit trail from onboarding to now.
                </p>
              </details>
            </section>
          </div>

          <footer class="help-foot">
            <span class="foot-note">
              Questions this guide didn't cover? Reach out to your account manager.
            </span>
            <button type="button" class="btn-close" @click="$emit('close')">Close</button>
          </footer>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script>
export default {
  name: "ledger-help-guide",
  emits: ["close"],
  props: {
    show: { type: Boolean, default: false },
    // 'branch' | 'client' — decides direction rules + noun choices.
    role: { type: String, required: true },
  },
  computed: {
    isClient() {
      return this.role === "client";
    },
    isBranch() {
      return this.role === "branch";
    },
    roleLabel() {
      return this.isClient ? "Client" : "Branch";
    },
    // Cosmetic — used inside sentences like "approved <orderNoun> orders".
    // Both roles handle pay-in and pay-out, so a generic term reads best.
    orderNoun() {
      return "pay-in / pay-out";
    },
    // Source-of-truth table for the direction rules. Mirrors the design
    // doc's table (project_ledger_v1_design.md): the branch column is
    // the mirror of the client column for pay-in / pay-out / commission,
    // because a branch physically holds the money the vendor safekeeps
    // for the client.
    directionRows() {
      if (this.isClient) {
        return [
          { event: "Pay-in approved",         direction: "credit", why: "Your account manager is safekeeping this money for you." },
          { event: "Pay-out approved",        direction: "debit",  why: "You're withdrawing from that safekept float." },
          { event: "Pay-in commission",       direction: "debit",  why: "Fee your account manager deducts on each pay-in." },
          { event: "Pay-out commission",      direction: "debit",  why: "Fee your account manager deducts on each pay-out." },
          { event: "Deposit (manual)",        direction: "credit", why: "You've added funds outside the order flow." },
          { event: "Withdrawal (manual)",     direction: "debit",  why: "You've taken funds out outside the order flow." },
          { event: "Bonus",                   direction: "credit", why: "Top-up your account manager issued in your favour." },
          { event: "Penalty",                 direction: "debit",  why: "Charge your account manager posted against your balance." },
        ];
      }
      // Branch — the mirror of client for pay-in / pay-out / commission.
      return [
        { event: "Pay-in approved",           direction: "debit",  why: "You now hold customer money on behalf of your account manager." },
        { event: "Pay-out approved",          direction: "credit", why: "You've paid on your account manager's behalf — they owe you back." },
        { event: "Pay-in commission",         direction: "credit", why: "Your earned commission on the pay-in." },
        { event: "Pay-out commission",        direction: "credit", why: "Your earned commission on the pay-out." },
        { event: "Deposit (manual)",          direction: "credit", why: "You've topped up your float." },
        { event: "Withdrawal (manual)",       direction: "debit",  why: "You've drawn down your float." },
        { event: "Bonus",                     direction: "credit", why: "Top-up your account manager issued in your favour." },
        { event: "Penalty",                   direction: "debit",  why: "Charge your account manager posted against your balance." },
      ];
    },
  },
};
</script>

<style scoped>
/* ==================== Overlay + panel ==================== */
.help-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  z-index: 200;
  display: flex;
  align-items: stretch;
  justify-content: flex-end;
}
.help-panel {
  background: #fff;
  width: 100%;
  max-width: 640px;
  height: 100%;
  display: flex;
  flex-direction: column;
  box-shadow: -20px 0 60px -30px rgba(15, 23, 42, 0.5);
  overflow: hidden;
}

/* ==================== Header ==================== */
.help-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 22px 24px 18px;
  border-bottom: 1px solid #ede9df;
  background: linear-gradient(180deg, #fbfaf7 0%, #ffffff 100%);
}
.help-eyebrow {
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #8a8371;
  margin-bottom: 4px;
}
.help-title {
  font-size: 22px;
  font-weight: 700;
  color: #1B2A47;
  margin: 0;
  letter-spacing: -0.01em;
  line-height: 1.15;
}
.help-sub {
  font-size: 13px;
  color: #64748b;
  margin: 4px 0 0;
  line-height: 1.4;
}
.help-close {
  background: transparent;
  border: none;
  color: #94a3b8;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
  flex-shrink: 0;
}
.help-close:hover { background: #f1ede2; color: #1B2A47; }
.help-close svg { width: 16px; height: 16px; }

/* ==================== TOC ==================== */
.help-toc {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  padding: 12px 24px;
  border-bottom: 1px solid #ede9df;
  background: #fbfaf7;
}
.toc-link {
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  text-decoration: none;
  padding: 4px 10px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid #ede9df;
  transition: all 0.12s;
}
.toc-link:hover {
  color: #1B2A47;
  background: #f4f0e5;
  border-color: #d9d3c1;
}

/* ==================== Body ==================== */
.help-body {
  padding: 20px 24px 24px;
  overflow-y: auto;
  flex: 1;
  scroll-behavior: smooth;
}
.help-section + .help-section { margin-top: 26px; }
.help-section h3 {
  font-size: 15px;
  font-weight: 700;
  color: #1B2A47;
  margin: 0 0 10px;
  letter-spacing: -0.005em;
  scroll-margin-top: 20px;
}
.help-section p {
  font-size: 13.5px;
  line-height: 1.6;
  color: #334155;
  margin: 0 0 10px;
}
.help-section p:last-child { margin-bottom: 0; }
.help-section b { color: #1B2A47; font-weight: 700; }

/* ---------- Hint cards ---------- */
.hint-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
  margin: 12px 0;
}
.hint-card {
  padding: 12px 14px;
  border-radius: 12px;
  background: #fbfaf7;
  border: 1px solid #ede9df;
}
.hint-label {
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #8a8371;
  margin-bottom: 4px;
}
.hint-card p {
  font-size: 12.5px;
  line-height: 1.55;
  color: #334155;
  margin: 0;
}

/* ---------- Callout ---------- */
.callout {
  padding: 12px 14px;
  border-radius: 10px;
  background: #f4f0e5;
  border: 1px solid #ede9df;
  font-size: 12.5px;
  line-height: 1.55;
  color: #334155;
  margin-top: 12px;
}
.callout-mute {
  background: #fbfaf7;
  color: #475569;
}

/* ---------- Direction table ---------- */
.dir-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12.5px;
  margin: 12px 0 0;
  border: 1px solid #ede9df;
  border-radius: 10px;
  overflow: hidden;
}
.dir-table thead {
  background: #fbfaf7;
}
.dir-table th {
  text-align: left;
  padding: 8px 12px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #64748b;
  border-bottom: 1px solid #ede9df;
}
.dir-table td {
  padding: 8px 12px;
  border-bottom: 1px solid #f4f0e5;
  color: #334155;
  vertical-align: top;
}
.dir-table tr:last-child td { border-bottom: none; }
.dir-event {
  font-weight: 600;
  color: #1B2A47;
  white-space: nowrap;
}
.dir-why { font-size: 12px; color: #475569; }
.dir-pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}
.dir-credit {
  background: #d1fae5;
  color: #047857;
  border: 1px solid #a7f3d0;
}
.dir-debit {
  background: #fee2e2;
  color: #b91c1c;
  border: 1px solid #fecaca;
}

/* ---------- Lists ---------- */
.cat-list, .tight-list {
  padding-left: 20px;
  margin: 0;
  font-size: 13px;
  line-height: 1.65;
  color: #334155;
}
.cat-list li, .tight-list li { margin-bottom: 6px; }
.cat-list li:last-child, .tight-list li:last-child { margin-bottom: 0; }

/* ---------- FAQ ---------- */
.faq {
  border: 1px solid #ede9df;
  border-radius: 10px;
  padding: 10px 14px;
  margin-bottom: 8px;
  background: #fff;
  transition: border-color 0.12s;
}
.faq[open] { border-color: #d9d3c1; }
.faq:last-child { margin-bottom: 0; }
.faq summary {
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: #1B2A47;
  list-style: none;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 2px 0;
}
.faq summary::-webkit-details-marker { display: none; }
.faq summary::before {
  content: "+";
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 5px;
  background: #f4f0e5;
  color: #1B2A47;
  font-weight: 700;
  font-size: 14px;
  transition: transform 0.15s;
}
.faq[open] summary::before {
  content: "−";
  background: #eef2f7;
}
.faq p {
  margin: 8px 0 0;
  font-size: 12.5px;
  line-height: 1.6;
  color: #475569;
}

/* ==================== Footer ==================== */
.help-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 24px;
  border-top: 1px solid #ede9df;
  background: #fbfaf7;
  flex-shrink: 0;
}
.foot-note {
  font-size: 11.5px;
  color: #8a8371;
  line-height: 1.4;
}
.btn-close {
  padding: 8px 16px;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #1B2A47;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.12s;
}
.btn-close:hover { background: #eef2f7; border-color: #94a3b8; }

/* ==================== Transitions ==================== */
.help-fade-enter-active,
.help-fade-leave-active {
  transition: opacity 0.2s ease;
}
.help-fade-enter-active .help-panel,
.help-fade-leave-active .help-panel {
  transition: transform 0.25s cubic-bezier(0.16, 0.84, 0.44, 1);
}
.help-fade-enter-from,
.help-fade-leave-to { opacity: 0; }
.help-fade-enter-from .help-panel,
.help-fade-leave-to .help-panel { transform: translateX(24px); }

/* ==================== Responsive ==================== */
@media (max-width: 640px) {
  .help-panel { max-width: 100%; }
  .help-head { padding: 18px 18px 14px; }
  .help-toc { padding: 10px 18px; gap: 6px; }
  .help-body { padding: 16px 18px 20px; }
  .help-foot { padding: 12px 18px; }
  .help-title { font-size: 20px; }
}
</style>
